import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { FormGroup } from '@angular/forms';

import { Plan, Prices, PromotionModel, TYPE_MARKETING } from '@bytel/bytel-sales';

import { ConfigurationService } from '@services/configuration.service';
import { MainCartService } from '@services/main-cart.service';
import { MessageService } from '@services/message.service';
import { OptionsService } from '@services/options.service';

import { AddPlanConfig } from '@interfaces/checkout.interface';

import { DestinationModalComponent } from '@shared/destination-modal/destination-modal.component';
import { ACTION_CLICK } from '@shared/tag/tag.interface';
import { TagService } from '@shared/tag/tag.service';

import { DataCyDirective } from '../../../../../common/directives/data-cy.directive';
import { ObligationPipe } from '../../../../../shared/pipes/obligation.pipe';
import { PricesBlockComponent } from '../../../subscription-block/subscription/prices-block/prices-block/prices-block.component';
import { ByouCardPepitesComponent } from '../byou-card-pepites/byou-card-pepites.component';
import { CardOfferHeaderByouComponent } from './card-offer-header-byou/card-offer-header-byou.component';

@Component({
    selector: 'checkout-card-offer-byou',
    templateUrl: './card-offer-byou.component.html',
    styleUrl: 'card-offer-byou.component.scss',
    changeDetection: ChangeDetectionStrategy.Default,
    standalone: true,
    imports: [
        DataCyDirective,
        PricesBlockComponent,
        NgClass,
        ObligationPipe,
        CardOfferHeaderByouComponent,
        ByouCardPepitesComponent,
    ],
})
export class CardOfferByouComponent implements OnInit {
    public isLocked: boolean = false;
    public qty: number;
    public prices: Prices;
    public useSpecialStyle: boolean = false;
    public promoHome: PromotionModel;
    public promoHomeWording: string = '';
    public isSummerEdition: boolean = false;

    @Input() public isLoading: boolean = false;
    @Input() public plan: Plan;
    @Input() public quoteIndex: number = 1;
    @Input() public cardStyle: 'unique' | 'multiple' = 'multiple';
    @Input() public showAction: boolean = true;
    @Input() public cartForm: FormGroup;
    @Input() public isUniq: boolean = false;
    @Input() public atLeastOneOfferHasPromoHome: boolean = false;
    @Input() public atLeastOneBlockablePlan: boolean = false;
    @Input() public hasHat: boolean = false;

    @Output() public addProductEvent = new EventEmitter<AddPlanConfig>();
    @Output() public removeProductEvent = new EventEmitter<string>();
    @Output() public setLockedEvent = new EventEmitter<boolean>();

    protected cartService: MainCartService = inject(MainCartService);
    protected messageService: MessageService = inject(MessageService);
    protected tagService = inject(TagService);
    protected configService = inject(ConfigurationService);

    public lockedToggle(): void {
        this.setLocked(!this.isLocked);
    }

    public setLocked(value: boolean): void {
        this.isLocked = value;
        this.setLockedEvent.emit(value);
    }

    public ngOnInit(): void {
        this.qty = this._getQty();
        this.cartService.cartQuoteSubscription.subscribe(() => {
            this.qty = this._getQty();
        });
        if (this.qty) {
            this.isLocked = OptionsService.HasBlockedOption(this.cartService.cartModel, this.quoteIndex);
        }
        this.promoHome = this.plan.promotions.automatic.find((promo) =>
            promo.typesMarketing.includes(TYPE_MARKETING.FOYER),
        );
        if (this.promoHome) {
            this.promoHomeWording = this.promoHome.fanion || `${-this.promoHome.amount}€/mois déjà déduit`;
        }
        this.isSummerEdition = this.plan.gencode === this.configService.getData().global.specialOffers.summerEdition;
    }

    public add(): void {
        this.sendTagAction('Choisir_ce_forfait');
        this.addProductEvent.emit({
            plan: this.plan,
            isLocked: this.isLocked,
        });
    }

    public view(): void {
        this.messageService.openModal(DestinationModalComponent, {
            isByou: true,
            isSummerEdition: this.isSummerEdition,
            title: this.isSummerEdition ? 'Destinations incluses sur Summer edition' : 'Destinations de la zone Europe',
        });
    }

    public remove(): void {
        this.sendTagAction('Supprimer');
        this.removeProductEvent.emit(this.plan.gencode);
        this.qty = 0;
    }

    private _getQty(): number {
        return this.cartService.getProductQty(this.plan);
    }

    private sendTagAction(clickName: string): void {
        this.tagService.sendEvent(
            `${clickName}_${this.plan.gencode}`,
            'Banniere_panier_mixte_forfait',
            ACTION_CLICK.SELECTION,
        );
    }
}
